/**
 * Normalize API payloads into shapes chart renderers expect.
 */

export const SERIES_COLORS = ["#E3DABB", "#93886D", "#434432"] as const;
export const SERIES_FADED_COLORS = ["#F9F8F1", "#F4F3F0", "#ECECEA"] as const;

/** Grouped bar / table (BarChart mode). */
export interface BarChartSeries {
  name: string;
  data: number[];
}

export interface BarChartTrailingColumn {
  label: string;
  values: string[];
}

export interface BarChartPayload {
  asOf: string;
  inceptionDate?: string;
  disclaimer?: string;
  benchmarkName?: string;
  periods: string[];
  series: BarChartSeries[];
  trailingColumn?: BarChartTrailingColumn;
}

export function normalizeBarChart(data: unknown): BarChartPayload {
  if (!data || typeof data !== "object") {
    return { asOf: "", periods: [], series: [] };
  }
  const o = data as Record<string, unknown>;
  const periods = Array.isArray(o.periods) ? o.periods.map(String) : [];
  const rawSeries = Array.isArray(o.series) ? o.series : [];
  const series: BarChartSeries[] = rawSeries
    .filter((s): s is Record<string, unknown> => s != null && typeof s === "object")
    .map((s) => ({
      name: String(s.name ?? ""),
      data: Array.isArray(s.data) ? s.data.map((n) => Number(n)) : [],
    }))
    .filter((s) => s.name && s.data.length > 0);
  let trailingColumn: BarChartTrailingColumn | undefined;
  const tc = o.trailingColumn;
  if (tc && typeof tc === "object") {
    const tco = tc as Record<string, unknown>;
    const label = typeof tco.label === "string" ? tco.label : "";
    const values = Array.isArray(tco.values) ? tco.values.map(String) : [];
    if (label && values.length) {
      trailingColumn = { label, values };
    }
  }

  return {
    asOf: String(o.asOf ?? ""),
    inceptionDate: typeof o.inceptionDate === "string" ? o.inceptionDate : undefined,
    disclaimer: typeof o.disclaimer === "string" ? o.disclaimer : undefined,
    benchmarkName: typeof o.benchmarkName === "string" ? o.benchmarkName : undefined,
    periods,
    series,
    trailingColumn,
  };
}

/** Line series point tuples. */
export interface LineChartSeries {
  name: string;
  data: [number, number][];
  endLabel?: string;
}

export interface LineChartTableRow {
  name: string;
  values: (number | string | null)[];
}

export interface LineChartAxisPadding {
  ratio?: number;
  minPadding?: number;
}

export interface LineChartAxisEdgeBound {
  fixed?: number;
  padding?: LineChartAxisPadding;
}

export interface LineChartYAxisBounds {
  min?: LineChartAxisEdgeBound;
  max?: LineChartAxisEdgeBound;
}

export interface LineChartPayload {
  asOf: string;
  inceptionDate?: string;
  disclaimer?: string;
  benchmarkName?: string;
  yAxisBounds?: LineChartYAxisBounds;
  series: LineChartSeries[];
  tableData?: {
    periods: string[];
    rows: LineChartTableRow[];
  };
}

function normalizeAxisEdgeBound(raw: unknown): LineChartAxisEdgeBound | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const o = raw as Record<string, unknown>;
  const edge: LineChartAxisEdgeBound = {};
  if (typeof o.fixed === "number" && Number.isFinite(o.fixed)) {
    edge.fixed = o.fixed;
  }
  if (o.padding && typeof o.padding === "object") {
    const pad = o.padding as Record<string, unknown>;
    const padding: LineChartAxisPadding = {};
    if (typeof pad.ratio === "number" && Number.isFinite(pad.ratio)) {
      padding.ratio = pad.ratio;
    }
    if (typeof pad.minPadding === "number" && Number.isFinite(pad.minPadding)) {
      padding.minPadding = pad.minPadding;
    }
    if (Object.keys(padding).length) {
      edge.padding = padding;
    }
  }
  if (edge.fixed === undefined && !edge.padding) {
    return undefined;
  }
  return edge;
}

/** Parse `yAxisBounds` from a dataset or catalog `options` object. */
export function normalizeLineChartYAxisBounds(raw: unknown): LineChartYAxisBounds | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const o = raw as Record<string, unknown>;
  const min = normalizeAxisEdgeBound(o.min);
  const max = normalizeAxisEdgeBound(o.max);
  if (!min && !max) return undefined;
  return { min, max };
}

export function lineChartYAxisBoundsFromSourceOptions(
  options: Record<string, unknown> | undefined
): LineChartYAxisBounds | undefined {
  if (!options) return undefined;
  return normalizeLineChartYAxisBounds(options.yAxisBounds);
}

/** Dataset bounds override catalog `options.yAxisBounds` per edge. */
export function mergeLineChartYAxisBounds(
  payload?: LineChartYAxisBounds,
  catalog?: LineChartYAxisBounds
): LineChartYAxisBounds | undefined {
  const min = payload?.min ?? catalog?.min;
  const max = payload?.max ?? catalog?.max;
  if (!min && !max) return undefined;
  return { min, max };
}

export function applyLineChartCatalogBounds(
  data: LineChartPayload,
  catalogOptions?: Record<string, unknown>
): LineChartPayload {
  const catalogBounds = lineChartYAxisBoundsFromSourceOptions(catalogOptions);
  const merged = mergeLineChartYAxisBounds(data.yAxisBounds, catalogBounds);
  if (!merged) return data;
  return { ...data, yAxisBounds: merged };
}

export function normalizeTimeSeriesTuples(data: unknown): [number, number][] {
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }
  const first = data[0] as unknown;
  if (Array.isArray(first) && first.length >= 2) {
    const a = first[0];
    const b = first[1];
    if (typeof a === "number" && typeof b === "number") {
      return data as [number, number][];
    }
  }
  if (typeof first === "object" && first !== null && "date" in first && "value" in first) {
    return (data as { date: number; value: number }[]).map((p) => [Number(p.date), Number(p.value)]);
  }
  return [];
}

export function normalizeLineChart(data: unknown): LineChartPayload {
  if (!data || typeof data !== "object") {
    return { asOf: "", series: [] };
  }
  const o = data as Record<string, unknown>;
  const rawSeries = Array.isArray(o.series) ? o.series : [];
  const series: LineChartSeries[] = rawSeries
    .filter((s): s is Record<string, unknown> => s != null && typeof s === "object")
    .map((s) => ({
      name: String(s.name ?? ""),
      data: normalizeTimeSeriesTuples(s.data),
      endLabel: typeof s.endLabel === "string" ? s.endLabel : undefined,
    }))
    .filter((s) => s.name && s.data.length > 0);

  let tableData: LineChartPayload["tableData"];
  const td = o.tableData;
  if (td && typeof td === "object") {
    const t = td as Record<string, unknown>;
    const periods = Array.isArray(t.periods) ? t.periods.map(String) : [];
    const rowsRaw = Array.isArray(t.rows) ? t.rows : [];
    const rows: LineChartTableRow[] = rowsRaw
      .filter((r): r is Record<string, unknown> => r != null && typeof r === "object")
      .map((r) => ({
        name: String(r.name ?? ""),
        values: Array.isArray(r.values) ? r.values.map((v) => (typeof v === "number" ? v : v == null ? null : String(v))) : [],
      }))
      .filter((r) => r.name);
    if (periods.length && rows.length) tableData = { periods, rows };
  }

  return {
    asOf: String(o.asOf ?? ""),
    inceptionDate: typeof o.inceptionDate === "string" ? o.inceptionDate : undefined,
    disclaimer: typeof o.disclaimer === "string" ? o.disclaimer : undefined,
    benchmarkName: typeof o.benchmarkName === "string" ? o.benchmarkName : undefined,
    yAxisBounds: normalizeLineChartYAxisBounds(o.yAxisBounds),
    series,
    tableData,
  };
}

export interface HorizontalBarSeries {
  name: string;
  data: number[];
}

export interface HorizontalBarChartPayload {
  asOf: string;
  subtitle?: string;
  categories: string[];
  series: HorizontalBarSeries[];
}

export function normalizeHorizontalBarChart(data: unknown): HorizontalBarChartPayload {
  if (!data || typeof data !== "object") {
    return { asOf: "", categories: [], series: [] };
  }
  const o = data as Record<string, unknown>;
  const categories = Array.isArray(o.categories) ? o.categories.map(String) : [];
  const rawSeries = Array.isArray(o.series) ? o.series : [];
  const series: HorizontalBarSeries[] = rawSeries
    .filter((s): s is Record<string, unknown> => s != null && typeof s === "object")
    .map((s) => ({
      name: String(s.name ?? ""),
      data: Array.isArray(s.data) ? s.data.map((n) => Number(n)) : [],
    }))
    .filter((s) => s.name && s.data.length > 0);
  return {
    asOf: String(o.asOf ?? ""),
    subtitle: typeof o.subtitle === "string" ? o.subtitle : undefined,
    categories,
    series,
  };
}

export interface AverageReturnsPeriod {
  label: string;
  subLabel?: string;
  net: number;
  pureGross: number;
}

export interface AverageReturnsPayload {
  asOf: string;
  ticker: string;
  disclaimer?: string;
  periods: AverageReturnsPeriod[];
}

export function normalizeAverageReturns(data: unknown): AverageReturnsPayload {
  if (!data || typeof data !== "object") {
    return { asOf: "", ticker: "", periods: [] };
  }
  const o = data as Record<string, unknown>;
  const raw = Array.isArray(o.periods) ? o.periods : [];
  const periods: AverageReturnsPeriod[] = raw
    .filter((p): p is Record<string, unknown> => p != null && typeof p === "object")
    .map((p) => ({
      label: String(p.label ?? ""),
      subLabel: typeof p.subLabel === "string" ? p.subLabel : undefined,
      net: Number(p.net),
      pureGross: Number(p.pureGross),
    }))
    .filter((p) => p.label && Number.isFinite(p.net) && Number.isFinite(p.pureGross));
  return {
    asOf: String(o.asOf ?? ""),
    ticker: String(o.ticker ?? ""),
    disclaimer: typeof o.disclaimer === "string" ? o.disclaimer : undefined,
    periods,
  };
}

export interface StatisticsRow {
  label: string;
  portfolio: string;
  benchmark: string;
}

export interface StatisticsPayload {
  asOf: string;
  frequency?: string;
  disclaimer?: string;
  rows: StatisticsRow[];
}

export function normalizeStatistics(data: unknown): StatisticsPayload {
  if (!data || typeof data !== "object") {
    return { asOf: "", rows: [] };
  }
  const o = data as Record<string, unknown>;
  const raw = Array.isArray(o.rows) ? o.rows : [];
  const rows: StatisticsRow[] = raw
    .filter((r): r is Record<string, unknown> => r != null && typeof r === "object")
    .map((r) => ({
      label: String(r.label ?? ""),
      portfolio: String(r.portfolio ?? ""),
      benchmark: String(r.benchmark ?? ""),
    }))
    .filter((r) => r.label);
  const disclaimer =
    typeof o.disclaimer === "string"
      ? o.disclaimer
      : typeof o.legalText === "string"
        ? o.legalText
        : undefined;
  return {
    asOf: String(o.asOf ?? ""),
    frequency: typeof o.frequency === "string" ? o.frequency : undefined,
    disclaimer,
    rows,
  };
}

export interface HoldingsRow {
  ticker: string;
  cusip: string;
  name: string;
  sector: string;
  weight: number;
  marketCap: string;
}

export interface HoldingsPayload {
  asOf?: string;
  subtitle?: string;
  disclaimer?: string;
  rows: HoldingsRow[];
}

export function normalizeHoldings(data: unknown): HoldingsPayload {
  if (!data || typeof data !== "object") {
    return { rows: [] };
  }
  const o = data as Record<string, unknown>;
  const raw = Array.isArray(o.rows) ? o.rows : [];
  const rows: HoldingsRow[] = raw
    .filter((r): r is Record<string, unknown> => r != null && typeof r === "object")
    .map((r) => ({
      ticker: String(r.ticker ?? ""),
      cusip: String(r.cusip ?? ""),
      name: String(r.name ?? ""),
      sector: String(r.sector ?? ""),
      weight: Number(r.weight),
      marketCap: String(r.marketCap ?? ""),
      lastPrice: r.lastPrice == null ? null : Number(r.lastPrice),
      priceChange: r.priceChange == null ? null : Number(r.priceChange),
    }))
    .filter((r) => r.ticker && r.name);
  return {
    asOf: typeof o.asOf === "string" ? o.asOf : undefined,
    subtitle: typeof o.subtitle === "string" ? o.subtitle : undefined,
    disclaimer: typeof o.disclaimer === "string" ? o.disclaimer : undefined,
    rows,
  };
}
